import { APP_PATHS } from '@/constant/app-paths';
import { SERVICE_REQUEST_PAGE_PERMISSIONS } from '@/constant/hr/manage-permissions';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { MenuItem, SidebarSection } from '@/types/sidebar.type';

/**
 * The main menu, grouped and named after the HR Module Navigation Map
 * (.claude/plans/prototype-revamp.md). `name` is a translation key in `generic`
 * (blocks/localization/generic.en-US.json), and `path` comes from APP_PATHS.
 *
 * Items marked "until ..." are pages that a later phase merges into another
 * page's tab; they stay in the menu until then so nobody loses their way in.
 * Item `id`s are stable internal names, not labels: keep them when renaming.
 */
const homeItems: MenuItem[] = [
  {
    id: 'hr-home',
    name: 'HOME',
    path: APP_PATHS.HOME,
    icon: 'House',
    permissions: [HRM_PERMISSIONS.HOME.VIEW],
  },
];

const peopleItems: MenuItem[] = [
  {
    id: 'hr-directory',
    name: 'PEOPLE',
    path: APP_PATHS.PEOPLE,
    icon: 'UsersThree',
    permissions: [HRM_PERMISSIONS.PEOPLE.VIEW],
  },
];

const timeAttendanceItems: MenuItem[] = [
  {
    id: 'hr-my-timesheet',
    name: 'TIME_TRACKER',
    path: APP_PATHS.TIME_TRACKER,
    icon: 'Timer',
    permissions: [HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TIME_TRACKER],
  },
  {
    id: 'hr-my-attendance',
    name: 'ATTENDANCE',
    path: APP_PATHS.ATTENDANCE,
    icon: 'Clock',
    permissions: [HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_ATTENDANCE],
  },
  {
    id: 'hr-manage-attendance',
    name: 'TEAM_ATTENDANCE',
    path: APP_PATHS.TEAM_ATTENDANCE,
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TEAM_ATTENDANCE],
  },
  {
    id: 'hr-timesheet-manager',
    name: 'TEAM_TIMESHEETS',
    path: APP_PATHS.TEAM_TIMESHEETS,
    icon: 'ListChecks',
    permissions: [HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TEAM_TIMESHEETS],
  },
];

const timeOffItems: MenuItem[] = [
  {
    id: 'hr-my-leave',
    name: 'MY_TIME_OFF',
    path: APP_PATHS.MY_TIME_OFF,
    icon: 'Airplane',
    permissions: [HRM_PERMISSIONS.TIME_OFF.VIEW_OWN],
  },
  {
    id: 'hr-leave-manager',
    name: 'TEAM_TIME_OFF',
    path: APP_PATHS.TEAM_TIME_OFF,
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.TIME_OFF.VIEW_TEAM],
  },
  {
    id: 'hr-leave-calendar',
    name: 'CALENDAR',
    path: APP_PATHS.TIME_OFF_CALENDAR,
    icon: 'CalendarDots',
    permissions: [HRM_PERMISSIONS.TIME_OFF.VIEW_CALENDAR],
  },
  {
    id: 'hr-sick-leave',
    name: 'SICK_LEAVE',
    path: APP_PATHS.SICK_LEAVE,
    icon: 'FirstAidKit',
    permissions: [HRM_PERMISSIONS.TIME_OFF.VIEW_SICK_LEAVE],
  },
];

const performanceItems: MenuItem[] = [
  // Requests and Submissions are its tabs (Phase 6); the dashboard opens from a review's details.
  {
    id: 'hr-my-reviews',
    name: 'MY_REVIEWS',
    path: APP_PATHS.MY_REVIEWS,
    icon: 'UsersThree',
    permissions: [HRM_PERMISSIONS.PERFORMANCE.VIEW_MY_REVIEWS],
  },
  {
    id: 'hr-reviews',
    name: 'ALL_REVIEWS',
    path: APP_PATHS.ALL_REVIEWS,
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.PERFORMANCE.REVIEWS_VIEW],
  },
];

const hiringItems: MenuItem[] = [
  {
    id: 'hr-potential-profiles',
    name: 'CANDIDATES',
    path: APP_PATHS.CANDIDATES,
    icon: 'IdentificationCard',
    permissions: [HRM_PERMISSIONS.HIRING.CANDIDATES_VIEW],
  },
  {
    id: 'hr-conditional-offers',
    name: 'OFFERS',
    path: APP_PATHS.OFFERS,
    icon: 'Handshake',
    permissions: [HRM_PERMISSIONS.HIRING.OFFERS_VIEW],
  },
];

const surveyItems: MenuItem[] = [
  {
    id: 'hr-surveys',
    name: 'ALL_SURVEYS',
    path: APP_PATHS.ALL_SURVEYS,
    icon: 'ChartBar',
    permissions: [HRM_PERMISSIONS.SURVEYS.VIEW],
  },
  {
    id: 'hr-survey-my-submissions',
    name: 'MY_RESPONSES',
    path: APP_PATHS.MY_SURVEY_RESPONSES,
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.SURVEYS.RESPONSES_VIEW_OWN],
  },
];

const mealItems: MenuItem[] = [
  {
    id: 'hr-request-lunch',
    name: 'ORDER_MEAL',
    path: APP_PATHS.ORDER_MEAL,
    icon: 'ForkKnife',
    permissions: [HRM_PERMISSIONS.MEAL.LUNCH_VIEW_OWN],
  },
  {
    id: 'hr-manage-lunch',
    name: 'MEAL_ORDERS',
    path: APP_PATHS.MEAL_ORDERS,
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.MEAL.LUNCH_VIEW_ALL],
  },
];

/** One page for every request type (Phase 3), opened by any of its tab permissions. */
const serviceRequestItems: MenuItem[] = [
  {
    id: 'hr-service-requests',
    name: 'SERVICE_REQUESTS',
    path: APP_PATHS.SERVICE_REQUESTS,
    icon: 'Headset',
    permissions: SERVICE_REQUEST_PAGE_PERMISSIONS,
  },
];

/**
 * The sidebar in display order. The sidebar renders these sections, and the route guard picks the
 * first permitted item across them as the landing page for a user who opens a URL they can't
 * access — so keep this order as the intended "home" priority.
 *
 * `icon` marks a section as a collapsible group (icon + label + chevron header that only toggles,
 * never navigates, with its items indented underneath). A section with no `icon` (People) renders
 * as a single flat link instead.
 */
export const hrSidebarSections: SidebarSection[] = [
  // Home is the default page (Component Map › Home), so it leads the landing order.
  { title: 'HOME', items: homeItems },
  { title: 'PEOPLE', items: peopleItems },
  { title: 'TIME_AND_ATTENDANCE', icon: 'Clock', items: timeAttendanceItems },
  { title: 'TIME_OFF', icon: 'Airplane', items: timeOffItems },
  { title: 'PERFORMANCE', icon: 'ChartLineUp', items: performanceItems },
  { title: 'HIRING', icon: 'IdentificationCard', items: hiringItems },
  { title: 'SURVEYS', icon: 'ChartBar', items: surveyItems },
  { title: 'MEAL', icon: 'ForkKnife', items: mealItems },
  { title: 'SERVICE_REQUESTS', items: serviceRequestItems },
];
